<script>
  import Modal from '../../../shared/ui/Modal/Modal.svelte'
  import DOMPurify from 'dompurify'
  import { onMount, onDestroy } from 'svelte'
  import { SESSION_TIMEOUT_MODAL as LABELS } from 'constants/textConstants'
  import { sessionTimeout } from 'stores/sessionTimeout'

  /**
   * @typedef {Object} Props
   * @property {boolean} [visible]
   * @property {number} [countdownSeconds]
   * @property {() => void} [onstay]
   * @property {() => void} [onlogoff]
   */

  /** @type {Props} */
  let { visible = $bindable(false), countdownSeconds = 120, onstay, onlogoff } = $props()

  let timeLeft = $state(120)
  let interval = null
  let timeoutId = null
  let wasVisible = false

  function clearTimers() {
    clearInterval(interval)
    clearTimeout(timeoutId)
    interval = null
    timeoutId = null
  }

  function closeModal() {
    clearTimers()
    visible = false
  }

  function staySignedIn() {
    closeModal()
    onstay?.()
    sessionTimeout.broadcastContinueSession()
  }

  function logOff() {
    closeModal()
    onlogoff?.()
    sessionTimeout.broadcastConfirmLogout()
  }

  function startCountdown() {
    timeLeft = countdownSeconds

    interval = setInterval(() => {
      timeLeft -= 1
      if (timeLeft <= 0) {
        logOff()
      }
    }, 1000)

    timeoutId = setTimeout(logOff, countdownSeconds * 1000)
  }

  const tabEventHandlers = {
    'session-show_warning': () => {
      if (!visible) visible = true
    },
    'session-confirm_logout': () => {
      if (visible) {
        closeModal()
        onlogoff?.()
      }
    },
    'session-continue_session': () => {
      if (visible) {
        closeModal()
        onstay?.()
      }
    },
  }

  $effect(() => {
    if (!visible) {
      if (wasVisible || interval) {
        wasVisible = false
        clearTimers()
        timeLeft = countdownSeconds
      }
      return
    }

    if (wasVisible) {
      return
    }

    wasVisible = true
    clearTimers()
    startCountdown()
    sessionTimeout.broadcastShowWarning()
  })

  onMount(() => {
    Object.entries(tabEventHandlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler)
    })
  })

  onDestroy(() => {
    clearTimers()

    Object.entries(tabEventHandlers).forEach(([event, handler]) => {
      window.removeEventListener(event, handler)
    })
  })
</script>

<Modal bind:visible title={LABELS.TITLE} onclose={staySignedIn}>
  {#snippet content()}
    <span>
      <p class="text-md text-gray-400 leading-6">
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html DOMPurify.sanitize(LABELS.CONTENT.replace('$TIME_LEFT', `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}`))}
      </p>
    </span>
  {/snippet}

  {#snippet footer()}
    <div class="flex justify-start space-x-3">
      <button class="px-4 py-2 rounded-md bg-petroleum-400 text-white hover:bg-blue-700 transition" onclick={staySignedIn}>{LABELS.CTA.STAY}</button>
      <button class="px-4 py-2 rounded-md border border-gray-400 text-gray-400 hover:bg-gray-100 transition" onclick={logOff}
        >{LABELS.CTA.LOGOFF}</button
      >
    </div>
  {/snippet}
</Modal>
